import React, { FunctionComponent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Heading,
  Text,
  Modal,
  Message,
  Tooltip,
  CARET_PLACEMENTS,
  Icon,
  Link,
  InputAddressData,
  useIsBreakpointMediumAndUp,
  useModal,
} from '@afterpaytouch/core'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import FormBuilder from '@afterpaytouch/form'
import { Trans, useTranslation } from 'next-i18next'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import styles from './style.module.css'
import { NonTerminalErrors, TrackingEvent } from '../../model/monitoring'
import {
  identityNationalIDNumber,
  setCheckoutError,
  setPaymentOptionsError,
  setShowPaymentOptionsErrorModal,
  useAppDispatch,
  useAppRouter,
  useConsumerLendingEligible,
  useConsumerSelector,
  useCountry,
  useIdentityNationalIDNumberDeprecatedMutation,
  useIsAfterpayFinanceFeeEligible,
  useIsPayMonthlyOnly,
  usePBIRejected,
  useStartCheckout,
  useSelectedPaymentType,
  useIsAfterpayAppGiftCardFlow,
  useIsPaymonthlySUPOrder,
} from '../../state'
import { getDOBValidations, getFormData, getSchema } from './utils'
import { IdentityVerificationForm, SchemaUpdates } from './types'
import { USER_VERIFICATION_ERRORS } from '../VerifyIdentity/constants'
import {
  Channel,
  IdentityNationalIDNumberError,
  IdentityNationalIDNumberRequest,
  IdentityNationalIDNumberResponse,
  IdentityStatus,
} from '@afterpaytouch/portal-api'
import { Route } from '../../config/router'
import { Features, IdentityVerificationModalKey, TerminalErrorCode } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import { useCloseCheckout } from '../../utils/close'
import clsx from 'clsx'
import { Feature, PaymentType } from '@afterpay/types'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage } from '../../integrations/inappbrowser-messenger'

interface IdentityVerificationModalProps {
  setClicked: (setClicked: boolean) => void
  setPbiOptionSelected?: (setPbiOptionSelected: boolean) => void
}

export const IdentityVerificationModal: FunctionComponent<IdentityVerificationModalProps> = ({ setClicked, setPbiOptionSelected = () => {} }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const closeCheckout = useCloseCheckout()
  const { doStartCheckoutAndHandleError } = useStartCheckout()
  const { headingSize, buttonSize } = useCheckoutSizes()
  const [lock, setLock] = useState(false)
  const [buttonLock, setButtonLock] = useState(false)
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const [hasIdentityGatewayError, setHasIdentityGatewayError] = useState<boolean>(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [isPoBoxError, setIsPoBoxError] = useState(false)
  const consumer = useConsumerSelector()
  const country = useCountry()
  const router = useAppRouter()
  const isDesktop = useIsBreakpointMediumAndUp() ?? false
  const buttonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLInputElement>(null)
  const tooltipRef = useRef<HTMLInputElement>(null)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [identityNationalIDNumberDeprecated] = useIdentityNationalIDNumberDeprecatedMutation()
  const enableFullNationalIdNumber = useFlag(Features.ENABLE_FULL_NATIONAL_ID_NUMBER, false)
  const { profile } = consumer
  const [showPatriotActModal, setShowPatriotActModal] = useState<boolean>(false)
  const pbiRejected = usePBIRejected()
  const [initialFormData] = useState(getFormData(consumer))
  const isPayMonthlyOnly = useIsPayMonthlyOnly()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const selectedPaymentType = useSelectedPaymentType()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const isAFFOnly = isAFF && !isConsumerLendingEligible
  const { visible, closeModal } = useModal(IdentityVerificationModalKey)
  const isSupPayMonthlyOrder = useIsPaymonthlySUPOrder()
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState({
    givenNames: false,
    otherGivenNames: false,
    surname: false,
    dateOfBirth: false,
    address: false,
    partialNationalIdNumber: false,
    fullNationalIdNumber: false,
  })
  const renderMode = useMemo(() => {
    if (isAFF) {
      return 'Native'
    }
    return isSupPayMonthlyOrder ? 'FullScreen' : undefined
  }, [isAFF, isSupPayMonthlyOrder])

  // These errors trigger an error message showing below the heading
  const hasVerificationFailedMessage =
    error === IdentityErrorCode.IdentityCheckRejected || error === IdentityErrorCode.DuplicateIdentity || hasIdentityGatewayError

  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    dobValidations: getDOBValidations(t, profile?.contactAddress as InputAddressData, country),
  })

  const closeTooltip = (): void => {
    setShowTooltip(false)
  }

  const toggleNationalIdTooltip = (): void => {
    const newState = !showTooltip
    if (!showTooltip) {
      logEvent(TrackingEvent.PRESSED_SSN_TOOLTIP)
    }
    setShowTooltip(newState)
  }

  const togglePatriotAct = (): void => {
    if (!showPatriotActModal) {
      logEvent(TrackingEvent.PRESSED_PATRIOT_ACT_LINK)
    }
    setShowPatriotActModal(!showPatriotActModal)
    // to remove the close button
    setLock(!lock)
  }

  useEffect(() => {
    if (showPatriotActModal) {
      containerRef.current?.scrollIntoView()
    } else {
      tooltipRef.current?.scrollIntoView()
    }
  }, [showPatriotActModal])

  const renderTooltipContent = (): ReactNode => {
    const usPatriotActNotification = <Link kind='Underline' testNameSpace='us-patriot-act-link' key='us-patriot-act-link' onClick={() => togglePatriotAct()} />

    return (
      <Text size='S'>
        <Trans components={{ usPatriotActNotification }} i18nKey='consumerLending:identityVerification.formLabels.partialNationalIdNumberTooltip' />
      </Text>
    )
  }

  const onAddressInputValueChange = (): void => {
    if (!hasSentAnalyticsData.address) {
      logEvent(TrackingEvent.CHANGED_ADDRESS)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, address: true })
    }
  }

  useEffect(() => {
    if (hasVerificationFailedMessage) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [hasVerificationFailedMessage])

  const { schema } = useMemo(() => {
    const components = {
      nationalIdNumberInfo: () => (
        <div className={styles.nationalIdNumberInfoContainer} data-popper-arrow>
          {showTooltip && (
            <span className={styles.tooltipContainer}>
              <Tooltip
                id='partial-national-number-id-tooltip'
                triggerRef={buttonRef}
                caretPlacement={CARET_PLACEMENTS.TOP_END}
                testNameSpace='payment-types-pcl-tooltip'
                content={renderTooltipContent()}
                usePortal={false}
                skidding={16}
                verticalOffset={0}
                useMobileView={!isDesktop}
                bubbleSize={{ width: '100%' }}
                ref={tooltipRef}
                onClose={closeTooltip}
                hasCaret={true}
                inline
              />
            </span>
          )}
        </div>
      ),
      footer: () => {
        return (
          <Button.Primary
            loading={lock}
            size={buttonSize}
            type='submit'
            disabled={buttonLock}
            padding='Fluid'
            onClick={() => logEvent(TrackingEvent.PRESSED_VERIFY_IDENTITY_BUTTON)}
          >
            {t('consumerLending:identityVerification.verifyIdentity')}
          </Button.Primary>
        )
      },
    }
    const isUserFormError = USER_VERIFICATION_ERRORS.includes(error as IdentityErrorCode)

    return getSchema(
      consumer,
      country,
      components,
      t,
      schemaUpdates,
      isUserFormError,
      isPoBoxError,
      setIsPoBoxError,
      logEvent,
      onAddressInputValueChange,
      enableFullNationalIdNumber,
      toggleNationalIdTooltip,
      buttonRef,
      isAFF
    )
  }, [
    consumer,
    country,
    schemaUpdates,
    t,
    buttonSize,
    lock,
    buttonLock,
    profile,
    error,
    showTooltip,
    isPoBoxError,
    hasSentAnalyticsData,
    enableFullNationalIdNumber,
    isAFF,
  ])

  const filterNationalIdData = (identityVerificationFormData: IdentityVerificationForm): IdentityNationalIDNumberRequest => {
    const { partialNationalIdNumber, fullNationalIdNumber, ...restData } = identityVerificationFormData
    if (enableFullNationalIdNumber) {
      return {
        ...restData,
        fullNationalIdNumber: fullNationalIdNumber.split('-').join(''),
      }
    } else {
      return {
        ...restData,
        partialNationalIdNumber,
      }
    }
  }

  const identityNationalIDNumberDeprecatedCall = async (
    identityVerificationFormData: IdentityNationalIDNumberRequest
  ): Promise<IdentityNationalIDNumberResponse | IdentityNationalIDNumberError> => {
    try {
      return await identityNationalIDNumberDeprecated({
        channel: Channel.CHECKOUT,
        ...identityVerificationFormData,
      }).unwrap()
    } catch (e) {
      return e
    }
  }

  const identityNationalIDNumberCall = async (
    identityVerificationFormData: IdentityVerificationForm,
    feature: Feature
  ): Promise<IdentityNationalIDNumberResponse | IdentityNationalIDNumberError> => {
    const requestData = filterNationalIdData(identityVerificationFormData)
    try {
      return await dispatch(
        identityNationalIDNumber({
          channel: Channel.CHECKOUT,
          feature,
          ...requestData,
        })
      ).unwrap()
    } catch (e) {
      if (e.status === 404) {
        return await identityNationalIDNumberDeprecatedCall(requestData)
      }
      return e
    }
  }

  const handleSuccessIdentityNationalIDNumber = async (
    response: IdentityNationalIDNumberResponse,
    currentFormData: IdentityVerificationForm
  ): Promise<void> => {
    const handleVerifiedPBI = (): void => {
      if (isConsumerLendingEligible) {
        setPbiOptionSelected(true)
      } else {
        closeModal()
      }
    }
    if (response.identityStatus === IdentityStatus.VERIFIED) {
      if (isAFF && selectedPaymentType !== PaymentType.PCL) {
        // Check if address states differ
        const consumerState = consumer.profile?.contactAddress?.state
        const verifiedState = currentFormData?.address.state

        if (consumerState !== verifiedState) {
          await doStartCheckoutAndHandleError()
        }
        handleVerifiedPBI()
      } else {
        router.push(Route.PAYMENT_OPTIONS)
      }
    } else if (response.identityStatus === IdentityStatus.REJECTED) {
      setLock(false)
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: NonTerminalErrors.IdentityCheckoutFailureError })
      setError(IdentityErrorCode.IdentityCheckRejected)
    }
  }

  const handleErrorIdentityNationalIDNumber = (errorResponse: IdentityNationalIDNumberError): void => {
    const error = getIdentityVerifyErrorCode(errorResponse)
    setLock(false)

    if (error === IdentityErrorCode.IdentityCheckGatewayError) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: NonTerminalErrors.IdentityServiceFailureError })
      setHasIdentityGatewayError(true)
      setButtonLock(false)
    } else {
      setError(error ?? IdentityErrorCode.Default)
    }

    if (error === IdentityErrorCode.MaxAttemptsReached) {
      if (isAFF) {
        dispatch(
          setCheckoutError({
            error: TerminalErrorCode.IdentityVerificationMaxAttemptsAFF,
            errorCode: TerminalErrorCode.IdentityVerificationMaxAttemptsAFF,
          })
        )
        postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_IDV_DECLINED)
        router.push(Route.TERMINAL_ERROR)
      } else if (pbiRejected) {
        dispatch(
          setCheckoutError({
            error: TerminalErrorCode.IdentityVerificationDeclinedPCL,
            errorCode: TerminalErrorCode.IdentityVerificationDeclinedPCL,
            extra: { payMonthlyOnly: isPayMonthlyOnly },
          })
        )
        router.push(Route.TERMINAL_ERROR)
      } else {
        dispatch(setShowPaymentOptionsErrorModal(true))
        dispatch(
          setPaymentOptionsError({
            error: TerminalErrorCode.IdentityVerificationDeclinedPCL,
            errorCode: errorResponse.data?.errorCode,
          })
        )
      }
    }
  }

  const containerClassName = clsx(styles.container, isAFF && showPatriotActModal && styles.native)

  const onSubmit = async (identityVerificationFormData: IdentityVerificationForm): Promise<void> => {
    setError(null)
    setLock(true)
    setButtonLock(true)
    setHasIdentityGatewayError(false)

    const feature = isAFF && selectedPaymentType !== PaymentType.PCL ? Feature.AFF : Feature.PCL
    const response = await identityNationalIDNumberCall(identityVerificationFormData, feature)

    if ((response as IdentityNationalIDNumberError)?.data) {
      handleErrorIdentityNationalIDNumber(response as IdentityNationalIDNumberError)
    } else {
      await handleSuccessIdentityNationalIDNumber(response as IdentityNationalIDNumberResponse, identityVerificationFormData)
    }
  }

  const onChange = (data: IdentityVerificationForm): void => {
    setButtonLock(false)
    setError(null)
    setHasIdentityGatewayError(false)

    if (data.givenNames !== initialFormData.givenNames && !hasSentAnalyticsData.givenNames) {
      logEvent(TrackingEvent.CHANGED_FIRST_NAME)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, givenNames: true })
    }
    if (data.otherGivenNames !== initialFormData.otherGivenNames && !hasSentAnalyticsData.otherGivenNames) {
      logEvent(TrackingEvent.CHANGED_MIDDLE_NAME)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, otherGivenNames: true })
    }
    if (data.surname !== initialFormData.surname && !hasSentAnalyticsData.surname) {
      logEvent(TrackingEvent.CHANGED_LAST_NAME)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, surname: true })
    }
    if (data.dateOfBirth !== initialFormData.dateOfBirth && !hasSentAnalyticsData.dateOfBirth) {
      logEvent(TrackingEvent.CHANGED_DOB)
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, dateOfBirth: true })
    }
    if (!enableFullNationalIdNumber && data.partialNationalIdNumber.length === 4 && !hasSentAnalyticsData.partialNationalIdNumber) {
      logEvent(TrackingEvent.SSN_FORM_FIELD_ENTERED, { partialNationalIdNumber: true })
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, partialNationalIdNumber: true })
    }
    if (enableFullNationalIdNumber && data.fullNationalIdNumber.length === 9 && !hasSentAnalyticsData.fullNationalIdNumber) {
      logEvent(TrackingEvent.SSN_FORM_FIELD_ENTERED, { fullNationalIdNumber: true })
      setHasSentAnalyticsData({ ...hasSentAnalyticsData, fullNationalIdNumber: true })
    }

    const { address } = data
    const dobValidations = getDOBValidations(t, address, country)

    setSchemaUpdates((currSchema) => ({
      ...currSchema,
      dobValidations,
    }))
  }

  return (
    <Modal
      id={IdentityVerificationModalKey}
      show={visible}
      showCloseButton={!lock}
      testNameSpace='identity-verification'
      onClose={() => {
        logEvent(TrackingEvent.PRESSED_CLOSE_CL_IDV_PAGE)
        closeTooltip()
        if (isAFFOnly) {
          closeCheckout()
          if (isAfterpayAppGiftCardFlow) {
            postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_GIFT_CARD_CLOSE)
          }
        } else {
          closeModal()
          setClicked(false)
        }
      }}
      horizontalScroll={false}
      renderMode={renderMode}
    >
      <div className={containerClassName} ref={containerRef}>
        {showPatriotActModal && (
          <div className={styles.halfScreen}>
            <Modal.Header>
              <div className={styles.patriotHeading}>
                <div>
                  <Button.Link
                    type='button'
                    size='Large'
                    aria-label='us-patriot-act-back'
                    testNameSpace='us-patriot-act-back'
                    onClick={() => togglePatriotAct()}
                  >
                    <Icon.ArrowLeft size='Small' />
                  </Button.Link>
                </div>
                <div>
                  <Heading size={headingSize}>{t('consumerLending:identityVerification.patriotActNotification.heading')}</Heading>
                </div>
              </div>
            </Modal.Header>
            <Modal.Content>
              <Text>{t('consumerLending:identityVerification.patriotActNotification.content')}</Text>
            </Modal.Content>
          </div>
        )}
        <div className={styles.halfScreen}>
          <Modal.Header>
            <Heading size={headingSize}>{t(`consumerLending:identityVerification.profileInformationTitle`)}</Heading>
            <div className={styles.profileInformationMessage}>
              <Text>{t('consumerLending:identityVerification.profileInformationMessage')}</Text>
            </div>
            {error === IdentityErrorCode.IdentityCheckRejected && (
              <div className={styles.verificationFailed}>
                <Message
                  kind='Error'
                  iconName='Profile'
                  heading={t(`consumerLending:identityVerification.verifyFailErrorLabels.title`)}
                  description={t(`consumerLending:identityVerification.verifyFailErrorLabels.message`)}
                />
              </div>
            )}
            {error === IdentityErrorCode.DuplicateIdentity && (
              <div className={styles.verificationFailed}>
                <Message
                  kind='Error'
                  iconName='Profile'
                  heading={t(`consumerLending:identityVerification.duplicateIdentityErrorLabels.title`)}
                  description={t(`consumerLending:identityVerification.duplicateIdentityErrorLabels.message`)}
                />
              </div>
            )}
            {hasIdentityGatewayError && (
              <div className={styles.verificationFailed}>
                <Message
                  kind='Error'
                  iconName='Profile'
                  heading={t(`consumerLending:identityVerification.serviceFailErrorLabels.title`)}
                  description={t(`consumerLending:identityVerification.serviceFailErrorLabels.message`)}
                />
              </div>
            )}
          </Modal.Header>
          <Modal.Content>
            <FormBuilder schema={schema} testNameSpace={'identity-verification'} onSubmit={onSubmit} onChange={onChange} disabled={lock} />
          </Modal.Content>
        </div>
        <TrackOnMount eventName={TrackingEvent.VIEWED_CL_IDV_PAGE} />
      </div>
    </Modal>
  )
}
